import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGameBusValue } from '../hooks/useGameBus'
import { GameBus } from '@game/core/GameBus'
import { useAuth } from '../hooks/useAuth'
import type { DummyBehavior } from '@shared/types'
import { useL10n } from '../hooks/useL10n'
import './TrainingHud.css'

const BEHAVIORS: DummyBehavior[] = ['idle', 'walk', 'defend', 'attack']

export default function TrainingHud() {
  const navigate = useNavigate()
  const { progress } = useAuth()
  const { t } = useL10n()

  const BEHAVIOR_LABELS: Record<DummyBehavior, string> = {
    idle: t('training.dummy_idle'),
    walk: t('training.dummy_walk'),
    defend: t('training.dummy_defend'),
    attack: t('training.dummy_attack'),
  }

  // Estado vindo do Phaser
  const hp = useGameBusValue('hp:changed', { hp: 100, maxHp: 100 })
  const axe = useGameBusValue('axe:changed', { axe: 0, maxAxe: 100 })
  const dummy = useGameBusValue('training:dummyHp', { hp: 1000, maxHp: 1000 })
  const combo = useGameBusValue('training:combo', {
    hits: 0,
    damage: 0,
    durationMs: 0,
    active: false,
  })
  const lastMoveEvt = useGameBusValue('training:lastMove', { name: '', ts: 0 })

  // Histórico dos últimos N golpes — derivado do evento sticky.
  const [history, setHistory] = useState<Array<{ name: string; ts: number }>>([])
  useEffect(() => {
    if (!lastMoveEvt.name) return
    setHistory((prev) => {
      const last = prev[prev.length - 1]
      if (last && last.ts === lastMoveEvt.ts) return prev
      return [...prev, lastMoveEvt].slice(-6)
    })
  }, [lastMoveEvt])

  // Configurações locais (UI). Estado é pequeno e reflete o que mandamos
  // ao Phaser via GameBus — não há "leitura de volta" porque a fonte da
  // verdade vive no TrainingScene.
  const [behavior, setBehavior] = useState<DummyBehavior>('idle')
  const [infiniteHp, setInfiniteHp] = useState(true)
  const [infiniteAxe, setInfiniteAxe] = useState(true)

  // Sincroniza configs iniciais com o Phaser na montagem.
  useEffect(() => {
    GameBus.emit('training:setInfiniteHp', { enabled: true })
    GameBus.emit('training:setInfiniteAxe', { enabled: true })
    GameBus.emit('training:setBehavior', { behavior: 'idle' })
  }, [])

  const onBehavior = (b: DummyBehavior) => {
    setBehavior(b)
    GameBus.emit('training:setBehavior', { behavior: b })
  }

  const onToggleInfHp = () => {
    const next = !infiniteHp
    setInfiniteHp(next)
    GameBus.emit('training:setInfiniteHp', { enabled: next })
  }

  const onToggleInfAxe = () => {
    const next = !infiniteAxe
    setInfiniteAxe(next)
    GameBus.emit('training:setInfiniteAxe', { enabled: next })
  }

  const onReset = () => GameBus.emit('training:reset', undefined)
  const onExit = () => navigate('/menu')

  const getPlayerPortrait = () => {
    const skinKey = (progress?.skin as string) ?? 'negro'
    if (skinKey === 'raiz' || skinKey === 'negro') return '/assets/ui/skin_negro.png'
    if (skinKey === 'dourado' || skinKey === 'loiro') return '/assets/ui/skin_loiro.png'
    if (skinKey === 'equilibrado') return '/assets/ui/skin_equilibrado.png'
    return '/assets/ui/skin_moreno.png'
  }

  return (
    <div className="thud">
      {/* Barras topo-esquerda: HP do jogador (∞ se infinito) e Axé */}
      <div className="thud__top-left">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
          <img
            src={getPlayerPortrait()}
            alt="Player Portrait"
            style={{ width: '48px', height: '48px', imageRendering: 'pixelated', marginRight: '8px', border: '2px solid rgba(255,255,255,0.3)', borderRadius: '4px', backgroundColor: 'rgba(0,0,0,0.5)' }}
          />
          <div style={{ color: '#fff', fontWeight: 'bold', fontSize: '1.2rem', textShadow: '1px 1px 2px #000' }}>PLAYER</div>
        </div>
        <Bar
          label="HP"
          color="#C76C3C"
          value={hp.hp}
          max={hp.maxHp}
          infinite={infiniteHp}
        />
        <Bar
          label="AXÉ"
          color="#E5B055"
          value={axe.axe}
          max={axe.maxAxe}
          infinite={infiniteAxe}
        />
        <Bar label="DUMMY" color="#88aaff" value={dummy.hp} max={dummy.maxHp} />
      </div>

      {/* Topo-direita: ações principais */}
      <div className="thud__top-right">
        <button type="button" className="thud__btn thud__btn--primary" onClick={onReset}>
          {t('training.restart')}
        </button>
        <button type="button" className="thud__btn" onClick={onExit}>
          {t('training.exit')}
        </button>
      </div>

      {/* Painel de combo (centro-topo) */}
      <div
        className={`thud__combo ${combo.active ? 'thud__combo--active' : ''} ${combo.hits === 0 ? 'thud__combo--idle' : ''}`}
      >
        <div className="thud__combo-row">
          <span className="thud__combo-num">{combo.hits}</span>
          <span className="thud__combo-suffix">{t('training.hits')}</span>
        </div>
        <div className="thud__combo-meta">
          <span>{combo.damage} {t('training.damage')}</span>
          <span>•</span>
          <span>{(combo.durationMs / 1000).toFixed(2)}s</span>
        </div>
      </div>

      {/* Histórico de comandos (centro-baixo, acima do gamepad) */}
      <div className="thud__history" aria-label="Últimos golpes executados">
        {history.length === 0 ? (
          <span className="thud__history-empty">{t('training.execute_move')}</span>
        ) : (
          history.map((h, i) => (
            <span
              key={`${h.ts}-${i}`}
              className={`thud__history-item ${i === history.length - 1 ? 'thud__history-item--latest' : ''}`}
            >
              {h.name}
            </span>
          ))
        )}
      </div>

      {/* Painel lateral direita: comportamento do dummy + toggles */}
      <div className="thud__side">
        <div className="thud__side-group">
          <div className="thud__side-title">{t('training.dummy')}</div>
          <div className="thud__behavior">
            {BEHAVIORS.map((b) => (
              <button
                key={b}
                type="button"
                className="thud__behavior-btn"
                aria-pressed={behavior === b}
                onClick={() => onBehavior(b)}
              >
                {BEHAVIOR_LABELS[b]}
              </button>
            ))}
          </div>
        </div>

        <div className="thud__side-group">
          <div className="thud__side-title">{t('training.resources')}</div>
          <label className="thud__check">
            <input type="checkbox" checked={infiniteHp} onChange={onToggleInfHp} />
            <span>{t('training.infinite_hp')}</span>
          </label>
          <label className="thud__check">
            <input type="checkbox" checked={infiniteAxe} onChange={onToggleInfAxe} />
            <span>{t('training.infinite_axe')}</span>
          </label>
        </div>
      </div>
    </div>
  )
}

interface BarProps {
  label: string
  value: number
  max: number
  color: string
  infinite?: boolean
}

function Bar({ label, value, max, color, infinite }: BarProps) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100))
  return (
    <div className="thud__bar-row">
      <span className="thud__bar-label">{label}</span>
      <div className="thud__bar-outer">
        <div
          className="thud__bar-inner"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
      <span className="thud__bar-value">{infinite ? '∞' : `${Math.round(value)}/${max}`}</span>
    </div>
  )
}
